import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { registrarEntradaAutomatica } from '@/lib/registroPonto';
import { tokenDeCliente } from '@/lib/portalClienteSessao';

// 'cliente' entrou no enum em 20261104154953; o types.ts ainda não foi regerado.
type AppRole = Database['public']['Enums']['app_role'] | 'cliente';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isColaborador: boolean;
  isLider: boolean;
  /** Pessoa do cliente, no Portal do Cliente. */
  isCliente: boolean;
  /** O token saiu com o papel do cliente (hook do token ligado). Sem isso, a guarda não deixa entrar. */
  clienteLiberado: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PRESENCE_PING_INTERVAL_MS = 5 * 60 * 1000;  // ping a cada 5 min com aba visível
const PRESENCE_THROTTLE_MS = 4 * 60 * 1000;       // throttle de 4 min entre pings
const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 horas de inatividade → logout

/** Eventos que só acontecem com alguém no teclado ou no mouse. */
const EVENTOS_DE_USO = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

/**
 * Sair encerra a sessão DESTE aparelho, e só ela.
 *
 * `signOut()` sem argumento é `scope: 'global'` no supabase-js: apaga no Auth
 * todas as sessões da pessoa. Quem usa o ECO no navegador e o ChatContar no
 * desktop tem duas, e qualquer saída no navegador — o botão Sair, ou o logout
 * por inatividade, que dispara de manhã ao abrir a aba de ontem — matava a do
 * desktop junto. O desktop não percebia: o token dele continua passando no
 * banco até vencer, porque o PostgREST só confere assinatura e validade. Quem
 * pergunta ao Auth se a sessão existe é a `chat-arquivo` (getClaims), e ela
 * respondia 401 — em 13/10/2026 as imagens do chat ficaram em "tentando de
 * novo…" no desktop enquanto abriam no navegador.
 */
const sairDesteAparelho = () => supabase.auth.signOut({ scope: 'local' });

/**
 * Por que a presença é gravada.
 *
 * 'presenca'  ping de intervalo ou volta do foco: diz que a aba está aberta, o
 *             que sobe sozinho a noite inteira num computador esquecido ligado.
 * 'interacao' tecla, clique, rolagem ou toque: diz que há gente usando.
 * 'login'     entrou agora com e-mail e senha.
 *
 * A diferença não é decorativa: o alerta de acesso fora do horário só avisa
 * sobre 'login' e 'interacao'. Ver supabase/functions/_shared/acessoForaHorario.ts.
 */
type MotivoPresenca = 'presenca' | 'interacao' | 'login';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [clienteLiberado, setClienteLiberado] = useState(false);
  const lastPresencePingRef = useRef(0);
  const lastInteracaoPingRef = useRef(0);
  /**
   * Front novo falando com banco que ainda não tem a coluna de interação.
   *
   * O deploy do front e a aplicação da migration são workflows separados e
   * podem ficar alguns minutos fora de sincronia. Desistir só da coluna nova
   * mantém a gravação de presença de pé — o "online agora" do chat e do painel
   * de TV não pode sair do ar por causa de uma coluna que só o alerta usa.
   */
  const semColunaDeInteracaoRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  const fetchRole = async (userId: string, accessToken?: string | null) => {
    // O cliente do Portal não lê user_roles (o papel dele no banco não tem
    // grant): é reconhecido pelo token. Ver src/lib/portalClienteSessao.ts.
    if (tokenDeCliente(accessToken)) {
      setRole('cliente');
      setClienteLiberado(true);
      return 'cliente' as AppRole;
    }
    setClienteLiberado(false);
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();
    // Papel 'cliente' com token comum: o hook do token não está ligado, e a
    // guarda tira a pessoa daqui (clienteLiberado falso).
    const nextRole: AppRole = (data?.role as AppRole | undefined) ?? 'comercial';
    setRole(nextRole);
    return nextRole;
  };

  const registerPresence = async (
    userId: string,
    options?: { force?: boolean; motivo?: MotivoPresenca },
  ) => {
    const nowTs = Date.now();
    const motivo = options?.motivo ?? 'presenca';
    const marcaUsoHumano = motivo !== 'presenca';

    // O uso humano tem throttle próprio. Com um só, um ping de intervalo
    // gravado há um minuto engoliria a interação seguinte pelos três minutos
    // restantes, e quem estava trabalhando apareceria como aba esquecida.
    const ultimoDoMotivo = marcaUsoHumano
      ? lastInteracaoPingRef.current
      : lastPresencePingRef.current;
    if (!options?.force && nowTs - ultimoDoMotivo < PRESENCE_THROTTLE_MS) {
      return;
    }

    lastPresencePingRef.current = nowTs;
    if (marcaUsoHumano) lastInteracaoPingRef.current = nowTs;

    const agora = new Date().toISOString();
    const linha: Database['public']['Tables']['user_presence']['Insert'] = {
      user_id: userId,
      last_seen_at: agora,
      user_agent: navigator.userAgent,
      updated_at: agora,
    };
    // Coluna omitida no upsert preserva o valor que já estava lá, e é isso que
    // deixa a última interação sobreviver aos pings de intervalo seguintes.
    if (marcaUsoHumano && !semColunaDeInteracaoRef.current) {
      linha.ultima_interacao_em = agora;
    }

    const { error } = await supabase
      .from('user_presence')
      .upsert(linha, { onConflict: 'user_id' });

    if (error) {
      // O throttle não volta atrás. Antes voltava, e com uma falha persistente
      // isso viraria uma requisição por tecla digitada, agora que a interação
      // também grava presença. A próxima tentativa é o ping seguinte — a
      // presença é redundante de propósito (intervalo, foco e interação).
      console.error('Erro ao registrar presença do usuário:', error);
      if (marcaUsoHumano && String(error.message ?? '').includes('ultima_interacao_em')) {
        semColunaDeInteracaoRef.current = true;
      }
    }
  };

  /**
   * Registro do login com e-mail e senha.
   *
   * Nada escrevia em `login_history` desde 15/04/2026, quando o registro de
   * atividade passou para `user_presence`. Volta a ser escrito porque é o único
   * sinal que diz "entrou agora": a sessão do Supabase persiste no navegador, e
   * sem este registro não há como distinguir acesso novo de sessão que já
   * estava aberta. Falhar aqui não pode atrapalhar o login.
   */
  const registrarLogin = async (userId: string) => {
    const { error } = await supabase.from('login_history').insert({
      user_id: userId,
      logged_in_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
    });
    if (error) console.error('Erro ao registrar o login do usuário:', error);
  };

  useEffect(() => {
    let initialised = false;

    // 1. Restore session from storage FIRST — this is the source of truth on mount
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      // Só no `npm run dev`: sem sessão, entra com o usuário de testes do
      // .env.local. Se entrou, o SIGNED_IN do listener abaixo assume.
      if (!session && import.meta.env.DEV) {
        const { entrarAutomaticamenteEmDev } = await import('@/lib/loginAutomaticoDev');
        if (await entrarAutomaticamenteEmDev()) return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      currentUserIdRef.current = session?.user?.id ?? null;
      if (session?.user) {
        await fetchRole(session.user.id, session.access_token);
      } else {
        setRole(null);
      }
      initialised = true;
      setLoading(false);
    });

    // 2. Listen for subsequent changes (sign-in, sign-out, token refresh)
    //    Skip INITIAL_SESSION events to avoid a flash of null before getSession resolves
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!initialised && _event === 'INITIAL_SESSION') return;
        // Eventos como TOKEN_REFRESHED/USER_UPDATED não devem disparar loading,
        // senão a página atual desmonta e remonta (parece "refresh sozinho").
        // Supabase também emite SIGNED_IN ao voltar de outra aba (visibilitychange);
        // se o user.id não mudou, tratamos como silencioso para não desmontar a página.
        const sameUser = !!currentUserIdRef.current && !!session?.user && currentUserIdRef.current === session.user.id;
        const isSilentEvent = _event === 'TOKEN_REFRESHED' || _event === 'USER_UPDATED' || (_event === 'SIGNED_IN' && sameUser);
        if (!isSilentEvent) setLoading(true);
        // Preserva a MESMA referência de user/session quando só houve refresh de token
        // (mesmo user.id). Sem isso, useEffect([user]) em dezenas de páginas rerodam
        // e recarregam dados sempre que o usuário troca de aba no navegador.
        setSession(prev => {
          if (isSilentEvent && prev && session && prev.user?.id === session.user?.id) return prev;
          return session;
        });
        setUser(prev => {
          const next = session?.user ?? null;
          if (isSilentEvent && prev && next && prev.id === next.id) return prev;
          return next;
        });
        currentUserIdRef.current = session?.user?.id ?? null;
        if (session?.user) {
          setTimeout(async () => {
            // Só refetch role em sign-in real, não em refresh de token
            if (!isSilentEvent) {
              await fetchRole(session.user.id, session.access_token);
            }
            if (!initialised) initialised = true;
            if (!isSilentEvent) setLoading(false);
          }, 0);
        } else {
          setRole(null);
          setLoading(false);
          if (!initialised) initialised = true;
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Inactivity auto-logout (8h) — persisted via localStorage.
  // Desabilitado no Electron: a janela fica em tray sem eventos de UI,
  // o que fazia o app cair de sessão sozinho ao reabrir.
  useEffect(() => {
    if (!user?.id) return;
    const isElectron = typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent);
    if (isElectron) return;
    // Nem no `npm run dev`: lá ele só devolve o preview parado à página de
    // entrada, que é o que o login automático (lib/loginAutomaticoDev) evita.
    // O build de produção troca DEV por false. Os testes seguem com o relógio.
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') return;

    const STORAGE_KEY = 'contar_last_activity';

    // Check on mount: if last activity was > 2h ago, sign out immediately
    const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (lastActivity > 0 && Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS) {
      localStorage.removeItem(STORAGE_KEY);
      sairDesteAparelho();
      return;
    }

    const updateActivity = () => {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    };

    let timeoutId: ReturnType<typeof setTimeout>;

    // Só o relógio da inatividade. A presença é gravada no efeito abaixo, que
    // escuta os mesmos eventos e não é desligado no Electron — se ficasse aqui,
    // o app de desktop nunca marcaria uso humano.
    const resetTimer = () => {
      updateActivity();
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        localStorage.removeItem(STORAGE_KEY);
        sairDesteAparelho();
      }, INACTIVITY_TIMEOUT_MS);
    };

    // mousemove removed — too sensitive, resets on any idle bump
    EVENTOS_DE_USO.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      EVENTOS_DE_USO.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user?.id]);

  useEffect(() => {
    // Presença é da equipe (chat, painel de TV, alerta de acesso fora do
    // horário); o cliente do Portal nem tem permissão na tabela.
    if (!user?.id || role === 'cliente') {
      lastPresencePingRef.current = 0;
      lastInteracaoPingRef.current = 0;
      return;
    }

    const syncPresence = () => {
      if (document.visibilityState === 'visible') {
        void registerPresence(user.id);
      }
    };

    // Uso humano, gravado em coluna própria. A montagem do app não conta: no
    // Electron ele pode subir junto com a máquina, e marcar interação aí traria
    // de volta o aviso de acesso sobre quem não acessou nada.
    const aoInteragir = () => {
      void registerPresence(user.id, { motivo: 'interacao' }); // honra throttle interno
    };

    syncPresence();

    const intervalId = window.setInterval(syncPresence, PRESENCE_PING_INTERVAL_MS);

    window.addEventListener('focus', syncPresence);
    EVENTOS_DE_USO.forEach(e => window.addEventListener(e, aoInteragir, { passive: true }));

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncPresence);
      EVENTOS_DE_USO.forEach(e => window.removeEventListener(e, aoInteragir));
    };
  }, [user?.id, role]);

  const isColaborador = role === 'colaborador' || role === 'lider';

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: 'Email ou senha inválidos' };
    
    // Log login history + auto-register ponto
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    // O cliente do Portal não deixa presença, histórico de login nem ponto:
    // são registros da equipe.
    const { data: { session: sessaoNova } } = await supabase.auth.getSession();
    if (currentUser && !tokenDeCliente(sessaoNova?.access_token)) {
      await Promise.all([
        registerPresence(currentUser.id, { force: true, motivo: 'login' }),
        registrarLogin(currentUser.id),
        registrarEntradaAutomatica(currentUser.id),
      ]);
    }
    return { error: null };
  };

  const signOut = async () => {
    if (import.meta.env.DEV) {
      const { pausarLoginAutomatico } = await import('@/lib/loginAutomaticoDev');
      pausarLoginAutomatico();
    }
    await sairDesteAparelho();
  };

  return (
    <AuthContext.Provider value={{
      user, session, role, loading, signIn, signOut,
      isAdmin: role === 'admin',
      isColaborador: role === 'colaborador' || role === 'lider',
      isLider: role === 'lider',
      isCliente: role === 'cliente',
      clienteLiberado,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
